/**
 * Association Salarié / Matériels — qui porte quel véhicule.
 *
 * *Chris, 10/09/2026 : « Pour gagner du temps il faudrait pouvoir associer du matériel à une main
 * d'œuvre, par exemple les VUL sont affectés à une personne […]. Tu peux créer un menu association
 * Salarié/Matériels. »*
 *
 * ## Cet écran ne crée AUCUNE ressource
 *
 * *« Si on veut créer des ressources on va dans les tables références »* (Chris, 10/09/2026). Les
 * deux listes viennent des référentiels d'archive-roy.app et rien ne s'y ajoute d'ici : on
 * **rapproche** deux fiches existantes. C'est aussi pourquoi les libellés affichés sont ceux du
 * référentiel vivant et non ce que la table a recopié — une machine recodée arrive toute seule.
 *
 * ## Ce que le référentiel maître dit DÉJÀ, et pourquoi on ne l'importe pas
 *
 * La fiche machine porte un champ `affectation_vehicule` qui contient souvent un nom. Mesuré le
 * 10/09/2026 sur les 56 VUL du parc : **23 correspondent exactement à un salarié**, 28 non — des
 * états (« UTILISABLE », « EN REPARATION », « ATELIER »), des formes mêlées (« BIVAUD Pascal /
 * BALISE HS »), une faute de frappe (« EN REPARATIUON »), et des noms qui ne sont plus au
 * référentiel des salariés. Le reprendre en lot poserait donc une trentaine de rapprochements
 * faux ou inventés. Il est **montré** comme une indication, jamais appliqué : la colonne « au
 * référentiel » dit ce que la fiche machine annonce, et le bouton propose la personne quand le
 * nom tombe juste. Un clic par véhicule, et aucune supposition écrite sans qu'on l'ait vue.
 */
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";

import {
  associerMateriel,
  chargerAssociations,
  chargerMateriels,
  chargerParc,
  chargerSalaries,
  retirerAssociation,
  type Association,
  type Materiel,
  type Salarie,
} from "./api";
import { designerMateriel, designerSalarie } from "./designerRessource";

type Etat =
  | { readonly phase: "chargement" }
  | { readonly phase: "erreur"; readonly raison: string }
  | {
      readonly phase: "chargee";
      readonly associations: readonly Association[];
      readonly salaries: readonly Salarie[];
      /** Les machines **affectables** : c'est `/api/materiels` qui écarte le parc sorti. */
      readonly materiels: readonly Materiel[];
      /**
       * Ce que la fiche de chaque machine annonce dans « affectation véhicule ».
       *
       * Il vient de `/api/parc`, la fiche complète, parce que `/api/materiels` ne porte pas ce
       * champ — et il n'a pas à le porter : il sert ici à **proposer**, jamais à affecter.
       */
      readonly auReferentiel: ReadonlyMap<string, string | null>;
    };

/** Ce que l'écran dit d'une association dont les référentiels ne répondent pas comme prévu. */
const DIRE_ETAT: Record<Association["etat"], string | null> = {
  resolue: null,
  hors_referentiel: "hors référentiel",
  referentiel_muet: "référentiel muet — copie",
};

/**
 * Le nom réduit à ce qui se compare : sans accents, sans ponctuation, mots triés.
 *
 * Trié parce que le champ libre du parc écrit tantôt « NOM Prénom », tantôt l'inverse, et que
 * l'ordre des deux mots n'est pas une différence de personne. Sans accents parce que « Frédéric »
 * et « Frederic » désignent le même homme et que le champ est saisi à la main.
 */
export function reduireLeNom(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z ]+/g, " ")
    .split(" ")
    .filter((mot) => mot !== "")
    .sort()
    .join(" ");
}

/**
 * Le salarié que le champ libre du parc désigne, s'il en désigne un sans ambiguïté.
 *
 * Rend `null` dès que la réduction ne tombe pas exactement sur une personne — « UTILISABLE »,
 * « ATELIER », un nom qui n'est plus au référentiel, ou deux homonymes. **Un rapprochement
 * approximatif serait pire que rien** : il proposerait un titulaire plausible que personne ne
 * vérifierait, et le véhicule suivrait la mauvaise personne sur les chantiers.
 */
export function salarieDesigne(
  texte: string | null,
  salaries: readonly Salarie[],
): Salarie | null {
  if (texte === null || texte.trim() === "") return null;
  const cherche = reduireLeNom(texte);
  if (cherche === "") return null;
  const trouves = salaries.filter((s) => reduireLeNom(`${s.nom} ${s.prenom}`) === cherche);
  return trouves.length === 1 ? (trouves[0] ?? null) : null;
}

export function Associations(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [salarieChoisi, setSalarieChoisi] = useState("");
  const [materielChoisi, setMaterielChoisi] = useState("");
  const [filtre, setFiltre] = useState("");

  const recharger = (): void => {
    Promise.all([chargerAssociations(), chargerSalaries(), chargerMateriels(), chargerParc()])
      .then(([a, s, m, p]) => {
        setEtat({
          phase: "chargee",
          associations: a.associations,
          salaries: s.salaries,
          materiels: m.materiels,
          auReferentiel: new Map(p.materiels.map((f) => [f.id, f.affectationVehicule])),
        });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, cle: string): void => {
    setOccupe(cle);
    setErreur(null);
    promesse
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  const chargee = etat.phase === "chargee" ? etat : null;

  /*
    Les machines déjà associées sortent de la liste de choix : une machine n'a qu'un titulaire,
    et la proposer quand même ferait cliquer pour se voir refuser. Le refus reste en place côté
    serveur — une liste ne contraint rien, elle suggère.
  */
  const prises = useMemo(
    () => new Set((chargee?.associations ?? []).map((a) => a.ressourceId)),
    [chargee],
  );

  const libres = useMemo(
    () => (chargee?.materiels ?? []).filter((m) => !prises.has(m.id)),
    [chargee, prises],
  );

  const filtrees = useMemo(() => {
    const cherche = filtre.trim().toLowerCase();
    if (cherche === "") return chargee?.associations ?? [];
    return (chargee?.associations ?? []).filter((a) =>
      `${a.salarieLibelle} ${a.ressourceLibelle} ${a.ressourceDetail ?? ""}`
        .toLowerCase()
        .includes(cherche),
    );
  }, [chargee, filtre]);

  return (
    <section className="associations">
      <p className="etat">
        Le matériel attaché à une personne : le véhicule qu'elle emmène. Dans le plan de charge
        exploitation, affecter quelqu'un pose <strong>en même temps</strong> son matériel, sur une
        ligne du chantier qui le chiffre ce jour-là. Les deux listes viennent des référentiels —
        pour créer un salarié ou une machine, c'est là-bas que ça se passe.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {etat.phase === "chargement" && <p>Chargement…</p>}
      {etat.phase === "erreur" && <p className="alerte">{etat.raison}</p>}

      {chargee !== null && (
        <>
          <div className="associations__ajout">
            <label>
              Salarié
              <select
                value={salarieChoisi}
                onChange={(e) => {
                  setSalarieChoisi(e.target.value);
                }}
              >
                <option value="">— choisir —</option>
                {chargee.salaries.map((s) => (
                  <option key={s.id} value={s.id}>
                    {designerSalarie(s)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Matériel
              <select
                value={materielChoisi}
                onChange={(e) => {
                  setMaterielChoisi(e.target.value);
                }}
              >
                <option value="">— choisir —</option>
                {libres.map((m) => (
                  <option key={m.id} value={m.id}>
                    {designerMateriel(m)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={salarieChoisi === "" || materielChoisi === "" || occupe !== null}
              onClick={() => {
                agir(associerMateriel(salarieChoisi, materielChoisi), "ajout");
                setSalarieChoisi("");
                setMaterielChoisi("");
              }}
            >
              Associer
            </button>
          </div>

          <h3>
            {chargee.associations.length} association
            {chargee.associations.length > 1 ? "s" : ""}
            <label className="associations__filtre">
              {" "}
              Chercher
              <input
                type="search"
                value={filtre}
                onChange={(e) => {
                  setFiltre(e.target.value);
                }}
                placeholder="un nom, un code"
              />
            </label>
          </h3>

          <div className="tableau-large">
            <table>
              <thead>
                <tr>
                  <th>Salarié</th>
                  <th>Poste</th>
                  <th>Matériel</th>
                  <th>Détail</th>
                  <th>Code générique</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtrees.map((a) => {
                  const dire = DIRE_ETAT[a.etat];
                  return (
                    <tr key={a.id} className={a.etat === "resolue" ? undefined : "en-ecart"}>
                      <td>
                        {a.salarieLibelle}
                        {dire !== null && <span className="note"> · {dire}</span>}
                      </td>
                      <td>{a.salariePoste ?? ""}</td>
                      <td>{a.ressourceLibelle}</td>
                      <td>{a.ressourceDetail ?? ""}</td>
                      {/*
                        Le code générique est ce qui VISE la ligne du plan de charge : sans lui,
                        rien ne sera posé, et il vaut mieux le voir ici que de chercher pourquoi
                        le véhicule n'apparaît pas.
                      */}
                      <td>{a.codeGenerique ?? <span className="note">aucun</span>}</td>
                      <td>
                        <button
                          type="button"
                          className="lien"
                          disabled={occupe !== null}
                          onClick={() => {
                            agir(retirerAssociation(a.id), a.id);
                          }}
                        >
                          retirer
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtrees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="vide">
                      {chargee.associations.length === 0
                        ? "Aucune association. Choisir un salarié et son matériel ci-dessus."
                        : "Aucune association ne correspond à cette recherche."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Proposees
            materiels={libres}
            auReferentiel={chargee.auReferentiel}
            salaries={chargee.salaries}
            occupe={occupe}
            onAssocier={(salarieId, materielId) => {
              agir(associerMateriel(salarieId, materielId), materielId);
            }}
          />
        </>
      )}
    </section>
  );
}

/**
 * Ce que le référentiel maître annonce déjà, machine par machine.
 *
 * **Une proposition, jamais une écriture.** Le champ `affectation_vehicule` de la fiche machine
 * est saisi à la main et mélange des titulaires et des états ; il n'y a donc pas de reprise en
 * lot, seulement une ligne par machine dont le nom tombe exactement sur un salarié, avec le
 * bouton qui l'associe. Les machines dont le champ ne désigne personne ne paraissent pas ici :
 * elles n'ont rien à proposer, et les lister ferait chercher une action dans une liste où il n'y
 * en a pas.
 */
function Proposees({
  materiels,
  auReferentiel,
  salaries,
  occupe,
  onAssocier,
}: {
  readonly materiels: readonly Materiel[];
  readonly auReferentiel: ReadonlyMap<string, string | null>;
  readonly salaries: readonly Salarie[];
  readonly occupe: string | null;
  readonly onAssocier: (salarieId: string, materielId: string) => void;
}): JSX.Element | null {
  const proposees = useMemo(
    () =>
      materiels
        .map((m) => ({
          materiel: m,
          texte: auReferentiel.get(m.id) ?? null,
          salarie: salarieDesigne(auReferentiel.get(m.id) ?? null, salaries),
        }))
        .filter(
          (p): p is { materiel: Materiel; texte: string; salarie: Salarie } => p.salarie !== null,
        ),
    [materiels, auReferentiel, salaries],
  );

  if (proposees.length === 0) return null;

  return (
    <>
      <h3>{proposees.length} proposition(s) d'après le référentiel du parc</h3>
      <p className="etat">
        Le champ « affectation véhicule » de la fiche machine porte un nom qui tombe exactement
        sur un salarié. Rien n'est écrit tant que vous n'avez pas cliqué : ce champ est saisi à la
        main et contient aussi des états (« UTILISABLE », « EN REPARATION »), qui ne paraissent
        pas ici.
      </p>
      <div className="tableau-large">
        <table>
          <thead>
            <tr>
              <th>Matériel</th>
              <th>Au référentiel</th>
              <th>Salarié reconnu</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {proposees.map(({ materiel, texte, salarie }) => (
              <tr key={materiel.id}>
                <td>{designerMateriel(materiel)}</td>
                <td>{texte}</td>
                <td>{designerSalarie(salarie)}</td>
                <td>
                  <button
                    type="button"
                    className="lien"
                    disabled={occupe !== null}
                    onClick={() => {
                      onAssocier(salarie.id, materiel.id);
                    }}
                  >
                    associer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
